/**
 * SettingsClosures — Closed days (workspace-wide + per-location overrides).
 * Table of closure rules: Add, edit, remove.
 */
import { useState, useEffect } from "react";
import { Banner, Btn, Dropdown, Field, Icon, IconButton, Input, Modal, Pill, T, Table } from "@v0y4ge/jrni-ui";
import type { DropdownOption, TableColumn } from "@v0y4ge/jrni-ui";
import { PageHeader } from "../../shell/chrome";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import SettingsShell from "./SettingsShell";
import { listClosedDays, createClosedDay, deleteClosedDay, listLocations } from "@v0y4ge/api-client";
import { LOCATIONS_OPTS, SETTINGS_OPTS } from "../../shell/request-opts";
import { useT } from "../../lib/i18n";

interface Closure { id:string; name:string; date:string; scope:"workspace"|string; type:"holiday"|"maintenance"|"custom"; }

const TYPE_TONES: Record<string,"neutral"|"info"|"warn"> = { holiday:"neutral", maintenance:"warn", custom:"info" };

function AddModal({ onSave, onClose, locationOptions }: { onSave:(c:Omit<Closure,"id">)=>void; onClose:()=>void; locationOptions:{id:string;name:string}[] }) {
  const t = useT();
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [scope, setScope] = useState<"workspace"|string>("workspace");
  const [type, setType] = useState<"holiday"|"maintenance"|"custom">("holiday");

  const scopeOptions: DropdownOption<string>[] = [
    { value:"workspace", label:t("settings.closures.allLocationsWorkspaceWide", "All locations (workspace-wide)") },
    ...locationOptions.map(l => ({ value:l.id, label:l.name })),
  ];
  const typeOptions: DropdownOption<"holiday"|"maintenance"|"custom">[] = [
    { value:"holiday", label:t("settings.closures.publicHoliday", "Public holiday") },
    { value:"maintenance", label:t("settings.closures.maintenanceClosure", "Maintenance closure") },
    { value:"custom", label:t("settings.closures.customBlockOut", "Custom block-out") },
  ];

  return (
    <Modal open onClose={onClose} title={t("settings.closures.addClosedDay", "Add closed day")} maxWidth={440}>
      <Modal.Body>
        <div style={{ display:"flex", flexDirection:"column", gap: T.space[4] }}>
          <Field label={t("settings.closures.name", "Name")}>
            <Input value={name} onChange={(e)=>setName(e.target.value)} placeholder={t("settings.closures.eGBankHoliday", "e.g. Bank Holiday")}/>
          </Field>
          <Field label={t("settings.closures.date", "Date")}>
            <Input value={date} onChange={(e)=>setDate(e.target.value)} placeholder={t("settings.closures.ddMmmYyyy", "DD MMM YYYY")} type="text"/>
          </Field>
          <Field label={t("settings.closures.appliesTo", "Applies to")}>
            <Dropdown options={scopeOptions} value={scope} onChange={setScope}/>
          </Field>
          <Field label={t("settings.closures.type", "Type")}>
            <Dropdown options={typeOptions} value={type} onChange={setType}/>
          </Field>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn disabled={!name.trim()||!date.trim()} onClick={() => { onSave({ name, date, scope, type }); onClose(); }}>{t("common.actions.add", "Add")}</Btn>
      </Modal.Footer>
    </Modal>
  );
}

export default function SettingsClosures() {
  const t = useT();
  const canWrite = can("settings.write");
  const [locOptionsCl, setLocOptionsCl] = useState<{id:string;name:string}[]>([]);
  useEffect(() => {
    listLocations({}, LOCATIONS_OPTS).then((r: any) => {
      setLocOptionsCl((r.locations ?? r.data ?? []).map((l: any) => ({ id: l.id, name: l.name ?? "" })));
    }).catch(() => {});
  }, []);
  const [closures, setClosures] = useState<Closure[] | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    listClosedDays({}, SETTINGS_OPTS).then((r: any) => {
      const arr = r.closures ?? r.data ?? r.days ?? [];
      setClosures(arr.map((c: any): Closure => ({ id: c.id, name: c.name, date: c.date ?? c.closureDate ?? "", scope: c.scope ?? c.locationId ?? "workspace", type: c.type ?? c.kind ?? "holiday" })));
    }).catch(() => setClosures([]));
  }, [nonce]);
  const [addOpen, setAddOpen] = useState(false);

  const columns: TableColumn<Closure>[] = [
    { id:"name", header:t("settings.closures.name", "Name"), cell:(c) => <span style={{ color:T.n1, fontWeight:600 }}>{c.name}</span> },
    { id:"date", header:t("settings.closures.date", "Date"), cell:(c) => <span style={{ color:T.n2, fontVariantNumeric:"tabular-nums" }}>{c.date}</span> },
    {
      id:"appliesTo",
      header:t("settings.closures.appliesTo", "Applies to"),
      cell:(c) => c.scope==="workspace"
        ? <span style={{ display:"inline-flex", alignItems:"center", gap: T.space[1], fontSize: T.text.label.fontSize, color:T.soft }}><Icon.globe size={11}/> {t("settings.closures.allLocations", "All locations")}</span>
        : <span style={{ display:"inline-flex", alignItems:"center", gap: T.space[1], fontSize: T.text.label.fontSize, color:T.n2 }}><Icon.loc size={11}/> {c.scope}</span>,
    },
    {
      id:"type",
      header:t("settings.closures.type", "Type"),
      cell:(c) => <Pill tone={TYPE_TONES[c.type]}>{c.type==="holiday"?t('settings.closures.text'):c.type==="maintenance"?t('settings.closures.text2'):t('settings.closures.text3')}</Pill>,
    },
    ...(canWrite ? [{
      id:"actions",
      header:"",
      minWidth:60,
      cell:(c: Closure) => (
        <IconButton
          label={t("common.actions.remove", "Remove")}
          size={28}
          onClick={() => { setClosures((prev)=>(prev ?? []).filter((x)=>x.id!==c.id)); toast({ tone:"warn", title:t("settings.closures.closedDayRemoved", "Closed day removed"), subtitle:c.name }); }}
        >
          <Icon.trash size={14}/>
        </IconButton>
      ),
    } as TableColumn<Closure>] : []),
  ];

  return (
    <SettingsShell activeTab="closures">
      <PageHeader
        title={t("settings.closures.closedDays", "Closed days")}
        subtitle={t("settings.closures.workspaceWideAndPerLocationClosuresNoBookingsOrEventsOnThese", "Workspace-wide and per-location closures — no bookings or events on these dates")}
        actions={canWrite ? <Btn icon={<Icon.plus/>} onClick={() => setAddOpen(true)}>{t("settings.closures.addClosedDay", "Add closed day")}</Btn> : null}
      />
      <div style={{ flex:1, overflow:"auto", padding: `${T.space[4]} ${T.space[6]} ${T.space[10]}` }}>
        <div style={{ maxWidth:800 }}>
          <Table
            columns={columns}
            rows={closures ?? []}
            getRowId={(c) => c.id}
            emptyState={t("settings.closures.noClosedDaysConfigured", "No closed days configured.")}
          />
          <Banner tone="primary" size="compact" style={{ marginTop: T.space[4] }} title={t("settings.closures.howClosuresWork", "How closures work:")}>
            {t("settings.closures.whenADateIsClosedTheBookingEngineBlocksAllNewReservations", "When a date is closed, the booking engine blocks all new reservations for that day at the applicable locations. Existing confirmed events are not affected — you'll need to contact guests manually if an event falls on a newly-added closure.")}
          </Banner>
        </div>
      </div>
      {addOpen ? <AddModal onSave={(c) => setClosures((prev)=>[...(prev ?? []),{ ...c, id:`cl${Date.now()}` }])} onClose={() => setAddOpen(false)} locationOptions={locOptionsCl} /> : null}
    </SettingsShell>
  );
}
